import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { MessageSquare, Send, Trash2, Edit, X } from 'lucide-react';
import { pb } from '@/lib/pocketbase';
import { moderateContent } from '@/utils/contentModeration';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { checkUserProfilePicture } from '@/utils/profileUtils';

interface Comment {
  id: string;
  content: string;
  userId: string;
  storyId: string;
  userName: string;
  userRole: string;
  created: string;
  updated: string;
  tutorRecord?: any;
  expand?: {
    userId?: any;
  };
}

interface TravelStoryCommentSectionProps {
  storyId: string;
  isOpen: boolean;
  onClose: () => void;
}

const TravelStoryCommentSection = ({ storyId, isOpen, onClose }: TravelStoryCommentSectionProps) => {
  const [newComment, setNewComment] = useState('');
  const [editingComment, setEditingComment] = useState<{ id: string; content: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);
  const navigate = useNavigate();

  // Get current user
  const currentUser = pb.authStore.model;

  // Fetch comments with tutor information
  const fetchComments = async () => {
    setIsLoadingComments(true);
    try {
      const records = await pb.collection('travel_story_comments').getFullList({
        filter: `storyId = "${storyId}"`,
        sort: '-created',
        expand: 'userId'
      });
      
      // For each comment, check if the user is a tutor
      const commentsWithTutorInfo = await Promise.all(
        records.map(async (record) => {
          try {
            const tutorRecords = await pb.collection('tutors').getFullList({
              filter: `user="${record.userId}"`,
              limit: 1
            });
            
            const comment: Comment = {
              id: record.id,
              content: record.content,
              userId: record.userId,
              storyId: record.storyId,
              userName: record.userName || 'Unknown User',
              userRole: record.userRole || 'student',
              created: record.created,
              updated: record.updated,
              expand: {
                userId: (record as any).expand?.userId
              }
            };
            
            if (tutorRecords.length > 0) {
              return { ...comment, tutorRecord: tutorRecords[0] };
            }
            return comment;
          } catch (error) {
            return {
              id: record.id,
              content: record.content,
              userId: record.userId,
              storyId: record.storyId,
              userName: record.userName || 'Unknown User',
              userRole: record.userRole || 'student',
              created: record.created,
              updated: record.updated
            };
          }
        })
      );
      
      setComments(commentsWithTutorInfo);
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Failed to load comments');
    } finally {
      setIsLoadingComments(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchComments();
    }
  }, [isOpen, storyId]);

  // Add comment handler
  const handleAddComment = async () => {
    if (!currentUser) {
      // Should not reach here as the textarea is hidden when not logged in
      return;
    }

    // Check if user has a profile picture
    const profile = await checkUserProfilePicture(currentUser.id, currentUser.role || 'student');
    if (!profile) {
      toast.error('Complete your profile first', {
        description: 'You need to complete your profile before adding comments.',
      });
      navigate('/profile');
      return;
    }

    const content = newComment.trim();
    if (!content) return;

    setIsSubmitting(true);
    try {
      // Content moderation
      const moderationResult = moderateContent(content);
      if (!moderationResult.isClean) {
        toast.error(moderationResult.message);
        setIsSubmitting(false);
        return;
      }

      await pb.collection('travel_story_comments').create({
        content,
        userId: currentUser.id,
        storyId,
        userName: currentUser.name,
        userRole: currentUser.role || 'student'
      });

      // Send notification to tutor
      try {
        const story = await pb.collection('travel_stories').getOne(storyId, {
          expand: 'tutorId'
        });
        
        if (story.expand?.tutorId && story.expand.tutorId.id !== currentUser.id) {
          const validUserId = story.expand.tutorId.id.length === 15 ? story.expand.tutorId.id : null;
          if (validUserId) {
            await pb.collection('notifications').create({
              userId: validUserId,
              type: 'comment',
              title: 'New Comment on Your Travel Story',
              message: `${currentUser.name} commented on your travel story "${story.title}"`,
              isRead: false,
              createdAt: new Date().toISOString(),
              data: {
                storyId: storyId,
                commenterName: currentUser.name
              }
            });
          }
        }
      } catch (error) {
        console.error('Failed to send comment notification:', error);
      }

      setNewComment('');
      fetchComments();
      toast.success('Comment posted successfully');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update comment handler
  const handleEditComment = async (commentId: string, newContent: string) => {
    if (!newContent.trim()) return;
    
    try {
      // Content moderation
      const moderationResult = moderateContent(newContent);
      if (!moderationResult.isClean) {
        toast.error(moderationResult.message);
        return;
      }

      // Verify comment exists
      const commentExists = comments.find(c => c.id === commentId);
      if (!commentExists) {
        toast.error('Comment not found. It may have been deleted.');
        fetchComments();
        return;
      }

      await pb.collection('travel_story_comments').update(commentId, { 
        content: newContent.trim() 
      });

      setEditingComment(null);
      fetchComments();
      toast.success('Comment updated successfully');
    } catch (error: any) {
      console.error('Error updating comment:', error);
      
      if (error?.status === 404) {
        toast.error('Comment not found. It may have been deleted.');
        fetchComments();
      } else if (error?.status === 403) {
        toast.error('You do not have permission to edit this comment.');
      } else {
        toast.error('Failed to update comment. Please try again.');
      }
      setEditingComment(null);
    }
  };

  // Delete comment handler
  const handleDeleteComment = async () => {
    if (!commentToDelete) return;
    
    try {
      // Verify comment exists
      const commentExists = comments.find(c => c.id === commentToDelete);
      if (!commentExists) {
        toast.error('Comment not found. It may have been already deleted.');
        setCommentToDelete(null);
        fetchComments();
        return;
      }

      await pb.collection('travel_story_comments').delete(commentToDelete);
      
      toast.success('Comment deleted successfully');
      setCommentToDelete(null);
      fetchComments();
    } catch (error: any) {
      console.error('Error deleting comment:', error);
      
      if (error?.status === 404) {
        toast.error('Comment not found. It may have been already deleted.');
        fetchComments();
      } else if (error?.status === 403) {
        toast.error('You do not have permission to delete this comment.');
      } else {
        toast.error('Failed to delete comment. Please try again.');
      }
      setCommentToDelete(null);
    }
  };


  const canModifyComment = (comment: Comment) => {
    if (!currentUser) return false;
    return currentUser.id === comment.userId || currentUser.role === 'admin';
  };

  const handleCommenterClick = (comment: Comment) => {
    const tutorRecord = comment.tutorRecord;
    if (tutorRecord?.id) {
      onClose();
      navigate(`/tutor/${tutorRecord.id}`);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent 
          className="max-w-2xl h-[98vh] max-h-[98vh] flex flex-col p-0"
          style={{ zIndex: 10000 }}
        >
          <div className="flex-shrink-0 bg-background border-b px-6 py-4">
            <DialogHeader className="space-y-0">
              <DialogTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Comments ({comments.length})
              </DialogTitle>
            </DialogHeader>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="absolute right-2 top-2 h-8 w-8 rounded-full opacity-70 hover:opacity-100 transition-opacity"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {/* Add Comment */}
            {currentUser && (
              <div className="flex gap-3 pb-4 border-b">
                <Textarea
                  placeholder="Write a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="min-h-[80px] resize-none"
                  dir="auto"
                  style={{ unicodeBidi: 'embed' }}
                />
                <Button
                  size="sm"
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || isSubmitting}
                  className="self-end"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Comments List */}
            <div className="space-y-4">
              {isLoadingComments ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading comments...
                </div>
              ) : comments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No comments yet. Be the first to comment!
                </div>
              ) : (
                comments.map(comment => {
                  const tutorRecord = comment.tutorRecord;
                  const commenter = comment.expand?.userId;
                  
                  // Get avatar - prioritize tutor profile picture
                  let avatarUrl = '';
                  if (tutorRecord) {
                    if (tutorRecord.profilePictureUrl) {
                      avatarUrl = tutorRecord.profilePictureUrl;
                    } else if (tutorRecord.profilePicture) {
                      try {
                        avatarUrl = pb.files.getURL(tutorRecord, tutorRecord.profilePicture);
                      } catch (error) {
                        console.error('Error getting file URL:', error);
                      }
                    }
                  }
                  
                  // Fallback to user avatar if no tutor avatar
                  if (!avatarUrl && commenter?.avatar) {
                    avatarUrl = commenter.avatar;
                  }
                  
                  const isClickable = !!tutorRecord?.id;
                  const isEditing = editingComment?.id === comment.id;
                  
                  return (
                    <div key={comment.id} className="flex gap-3">
                      <Avatar 
                        className={`h-9 w-9 flex-shrink-0 ${isClickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                        onClick={isClickable ? () => handleCommenterClick(comment) : undefined}
                      >
                        {avatarUrl && <AvatarImage src={avatarUrl} alt={comment.userName} />}
                        <AvatarFallback>
                          {comment.userName.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 bg-muted rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <p 
                            className={`font-medium text-sm ${isClickable ? 'cursor-pointer hover:text-primary transition-colors' : ''}`}
                            onClick={isClickable ? () => handleCommenterClick(comment) : undefined}
                          >
                            {comment.userName}
                          </p>
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(comment.created), { addSuffix: true })}
                            </p>
                            {canModifyComment(comment) && !isEditing && (
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setEditingComment({ id: comment.id, content: comment.content })}
                                  className="h-6 w-6 p-0"
                                >
                                  <Edit className="h-3 w-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setCommentToDelete(comment.id)}
                                  className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                        {isEditing ? (
                          <div className="space-y-2">
                            <Textarea
                              value={editingComment.content}
                              onChange={(e) => setEditingComment({ ...editingComment, content: e.target.value })}
                              className="min-h-[60px] text-sm"
                              dir="auto"
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => handleEditComment(comment.id, editingComment.content)}
                                disabled={!editingComment.content.trim()}
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingComment(null)}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm whitespace-pre-wrap" dir="auto">
                            {comment.content}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!commentToDelete} onOpenChange={() => setCommentToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Comment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this comment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteComment} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default TravelStoryCommentSection;